import RoundedButton from "@/components/rounded-button"
import SingleReading from "@/components/single-reading"
import Combobox from "@/components/ui/combobox"
import { Icon } from "@/icons/icon"
import { createUuid } from "@/lib/utils"
import useStore from "@/store"
import { RMTruck } from "@/store/rmpm"
import { LabelValue } from "@/types/common"
import { useCallback, useMemo, useRef, useState } from "react"

export interface RMFormProps {
  trucks: RMTruck[]
  defaultValue: RMTruck[]
  onChange?: (trucks: RMTruck[]) => void
}

export default function RMForm({
  trucks,
  defaultValue,
  onChange,
}: RMFormProps) {
  const rmpmStore = useStore.use.rmpm()

  const rmProducts = useMemo<LabelValue[]>(() => {
    return rmpmStore.allProducts.rmProducts.map((product) => ({
      id: product.id,
      label: product.name,
      value: product.id,
    }))
  }, [rmpmStore.allProducts])
  const localTrucks = useTrucks()

  function useTrucks() {
    const [localTrucks, setLocalTrucks] = useState<RMTruck[]>(
      trucks.length
        ? trucks
        : [
          {
            id: createUuid(),
            truckNumber: "",
            products: [],
          },
        ]
    )

    function add(truck?: RMTruck) {
      let newTrucks: RMTruck[] = []
      if (truck) {
        newTrucks = [
          ...localTrucks,
          {
            ...truck,
            id: createUuid(),
          },
        ]
      } else {
        newTrucks = [
          ...localTrucks,
          {
            id: createUuid(),
            truckNumber: "",
            products: [],
          },
        ]
      }

      emitChange(newTrucks)
      setLocalTrucks(newTrucks)
    }

    function set(trucks: RMTruck[]) {
      emitChange(trucks)
      setLocalTrucks(trucks)
    }

    function update(newTruck: Partial<RMTruck>) {
      const newTrucks = localTrucks.map((truck) => {
        if (truck.id === newTruck.id) {
          return {
            ...truck,
            ...newTruck,
          }
        }

        return truck
      })

      emitChange(newTrucks)
      setLocalTrucks(newTrucks)
    }

    function remove(id: string) {
      const newTrucks = localTrucks.filter((truck) => truck.id !== id)

      emitChange(newTrucks)
      setLocalTrucks(newTrucks)
    }

    function emitChange(trucks: RMTruck[]) {
      if (onChange) {
        onChange(trucks)
      }
    }

    return {
      trucks: localTrucks,
      add,
      set,
      remove,
      update,
    }
  }

  function isDisabled(truckId: string) {
    return !!defaultValue.find((truck) => truck.id === truckId)
  }

  function handleChangeTruck(truck: RMTruck) {
    localTrucks.update(truck)
  }

  function handleAddTruck() {
    localTrucks.add()
  }

  function handleClickDelete() {
    localTrucks.remove(localTrucks.trucks.at(-1)?.id || "")
  }

  return (
    <div className="px-4 py-8">
      <h2 className="mb-6 font-semibold text-2xl">RM</h2>

      {localTrucks.trucks.map((truck, index) => (
        <div key={truck.id}>
          {index > 0 && (
            <hr className="my-4 h-0 border-t border-t-input-border w-full" />
          )}

          <TruckRow
            details={truck}
            products={rmProducts}
            disabled={isDisabled(truck.id)}
            onChange={handleChangeTruck}
          />
        </div>
      ))}

      <div className="flex items-center justify-end gap-6 mt-6">
        {localTrucks.trucks.length > 1 && (
          <button
            className="flex gap-2 not-disabled:hover:cursor-pointer"
            type="button"
            onClick={handleClickDelete}
          >
            <Icon name="TrashCan" className="text-icon h-4 w-auto" />

            <span className="text-button text-xs font-semibold">
              Delete Entry
            </span>
          </button>
        )}

        <RoundedButton icon={<Icon name="Plus" />} onClick={handleAddTruck}>
          Truck
        </RoundedButton>
      </div>
    </div>
  )
}

interface TruckRowProps {
  details: RMTruck
  products: LabelValue[]
  disabled?: boolean
  onChange?: (truck: RMTruck) => void
}

function TruckRow({ details, products, disabled, onChange }: TruckRowProps) {
  const [truckNumber, setTruckNumber] = useState(details.truckNumber || "")
  const [selectedProducts, setSelectedProducts] = useState<string[]>(details.products || [])

  const truckNumberRef = useRef(truckNumber)
  const selectedProductsRef = useRef(selectedProducts)

  const emitChange = useCallback((truckNum: string, prods: string[]) => {
    onChange?.({
      id: details.id,
      truckNumber: truckNum,
      products: prods,
    })
  }, [details.id, onChange])

  const handleChangeTruckNumber = useCallback((newTruckNumber: string) => {
    setTruckNumber(newTruckNumber)
    truckNumberRef.current = newTruckNumber
    emitChange(newTruckNumber, selectedProductsRef.current)
  }, [emitChange])

  const handleChangeProducts = useCallback((newProducts: string[]) => {
    setSelectedProducts(newProducts)
    selectedProductsRef.current = newProducts
    emitChange(truckNumberRef.current, newProducts)
  }, [emitChange])

  return (
    <div className="grid grid-cols-2 gap-4">
      <SingleReading
        label="Truck Number"
        direction="column"
        value={truckNumber}
        disabled={disabled}
        onChange={handleChangeTruckNumber}
      />
      <Combobox
        label="Products"
        options={products}
        multiple
        defaultValue={selectedProducts}
        disabled={disabled}
        onChange={handleChangeProducts}
      />
    </div>
  )
}
